import React, { useState, useEffect, useMemo, useCallback, useId } from 'react';
import axiosInstance from '../config/axios';
import { ArrowLeftRight, Send, CheckCircle, XCircle, Loader2, Trash2 } from 'lucide-react';
import { compareBatchItemsByFriendlyDisplay } from '../utils/inventoryDisplay';
import './ManagerTransfers.css';

const TRANSFER_BLOCKED_SITE_MSG =
  'This site has already completed their inventory count. Stock transfers are no longer possible for this site.';

/** True when catalog row has a second unit: non-null unit2Id and non-empty enriched unit2 name. */
function inventoryHasUnit2(item) {
  if (!item) return false;
  const id = item.unit2Id ?? item.unit2_id;
  if (id == null || String(id).trim() === '') return false;
  const name = item.unit2 != null ? String(item.unit2).trim() : '';
  return name.length > 0;
}

const ManagerTransfers = ({ managerSites = [], allInventory = [], brands = [], user, completedSiteIds = [], onTransfersChanged }) => {
  const [tenantSites, setTenantSites] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [recipientSiteId, setRecipientSiteId] = useState('');
  const [senderSiteId, setSenderSiteId] = useState('');
  const [inventoryItemId, setInventoryItemId] = useState('');
  const [quantity1, setQuantity1] = useState('');
  const [quantity2, setQuantity2] = useState('');

  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [removeTransferId, setRemoveTransferId] = useState(null);
  const [removing, setRemoving] = useState(false);
  const qty1InputId = useId();
  const qty2InputId = useId();

  const loadAll = async () => {
    setError('');
    try {
      const [tsRes, trRes] = await Promise.all([
        axiosInstance.get('/api/tenant/stock-transfers/tenant-sites'),
        axiosInstance.get('/api/tenant/stock-transfers')
      ]);
      setTenantSites(tsRes.data.sites || []);
      setTransfers(trRes.data.transfers || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load transfers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const completedSiteIdSet = useMemo(() => new Set(completedSiteIds || []), [completedSiteIds]);

  const brandNameById = useMemo(() => {
    const m = {};
    for (const b of brands || []) {
      if (b && b.id != null) m[b.id] = (b.name && String(b.name).trim()) ? b.name : String(b.id);
    }
    return m;
  }, [brands]);

  const trySelectSite = useCallback(
    (siteId, setSiteState, extra) => {
      if (siteId && completedSiteIdSet.has(siteId)) {
        window.alert(TRANSFER_BLOCKED_SITE_MSG);
        return;
      }
      setSiteState(siteId);
      if (typeof extra === 'function') extra();
    },
    [completedSiteIdSet]
  );

  const recipientSite = useMemo(() => managerSites.find((s) => s.id === recipientSiteId), [managerSites, recipientSiteId]);
  const brandId = recipientSite?.brandId;

  const productOptions = useMemo(() => {
    if (!brandId) return [];
    const list = (allInventory || []).filter((i) => String(i.brandId) === String(brandId));
    return [...list].sort(compareBatchItemsByFriendlyDisplay);
  }, [allInventory, brandId]);

  const selectedInventoryItem = useMemo(() => {
    if (!inventoryItemId) return null;
    return productOptions.find((i) => String(i.id) === String(inventoryItemId)) || null;
  }, [productOptions, inventoryItemId]);

  const unit1Name = selectedInventoryItem && String(selectedInventoryItem.unit1 || '').trim();
  const unit2Name = selectedInventoryItem && String(selectedInventoryItem.unit2 || '').trim();
  const showUnit1Pill = Boolean(unit1Name);
  const hasUnit2 = inventoryHasUnit2(selectedInventoryItem);

  useEffect(() => {
    if (!hasUnit2) setQuantity2('');
  }, [hasUnit2]);

  const senderOptions = useMemo(() => {
    return (tenantSites || [])
      .filter((s) => s.id !== recipientSiteId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [tenantSites, recipientSiteId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (user?.role !== 'manager') {
      setError('Only managers can create transfer requests');
      return;
    }
    if (!recipientSiteId || !senderSiteId || !inventoryItemId) {
      setError('Choose recipient site, sender site, and a product');
      return;
    }
    if (completedSiteIdSet.has(recipientSiteId) || completedSiteIdSet.has(senderSiteId)) {
      window.alert(TRANSFER_BLOCKED_SITE_MSG);
      return;
    }
    const q1 = String(quantity1 || '').trim();
    if (q1 === '') {
      setError('Enter quantity (unit 1)');
      return;
    }
    setSubmitting(true);
    try {
      await axiosInstance.post('/api/tenant/stock-transfers', {
        recipientSiteId,
        senderSiteId,
        inventoryItemId,
        quantity1: q1,
        quantity2: quantity2.trim() === '' ? undefined : quantity2
      });
      setSuccess('Transfer request submitted. The sending site manager has been notified.');
      setInventoryItemId('');
      setQuantity1('');
      setQuantity2('');
      await loadAll();
      onTransfersChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const uid = user?.id;
  const isManager = user?.role === 'manager';

  const pendingList = transfers.filter((t) => t.status === 'pending');
  const historyList = transfers.filter((t) => t.status !== 'pending');

  const approve = async (id) => {
    setError('');
    try {
      await axiosInstance.post(`/api/tenant/stock-transfers/${id}/approve`);
      setSuccess('Transfer approved.');
      await loadAll();
      onTransfersChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to approve');
    }
  };

  const reject = async () => {
    if (!rejectId) return;
    setError('');
    try {
      await axiosInstance.post(`/api/tenant/stock-transfers/${rejectId}/reject`, { rejectionReason: rejectReason });
      setSuccess('Transfer rejected.');
      setRejectId(null);
      setRejectReason('');
      await loadAll();
      onTransfersChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to reject');
    }
  };

  const confirmRemoveTransfer = async () => {
    if (!removeTransferId) return;
    setError('');
    setSuccess('');
    setRemoving(true);
    try {
      await axiosInstance.post(`/api/tenant/stock-transfers/${removeTransferId}/remove-by-sender`);
      setSuccess('Transfer removed.');
      setRemoveTransferId(null);
      await loadAll();
      onTransfersChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to remove transfer');
    } finally {
      setRemoving(false);
    }
  };

  const cancel = async (id) => {
    setError('');
    try {
      await axiosInstance.post(`/api/tenant/stock-transfers/${id}/cancel`);
      setSuccess('Transfer request cancelled.');
      await loadAll();
      onTransfersChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to cancel');
    }
  };

  if (loading) {
    return (
      <div className="mgr-transfer-loading">
        <Loader2 className="mgr-transfer-spin" size={28} />
        <span>Loading transfers…</span>
      </div>
    );
  }

  return (
    <div className="mgr-transfers">
      <div className="mgr-transfers-header">
        <h2 className="mgr-transfers-title">
          <ArrowLeftRight size={22} /> Stock transfers
        </h2>
      </div>

      {error && <div className="mgr-banner mgr-banner-error mgr-transfer-banner">{error}</div>}
      {success && <div className="mgr-banner mgr-banner-success mgr-transfer-banner">{success}</div>}

      {isManager && (
        <form className="card mgr-transfer-form" onSubmit={handleSubmit}>
          <h3 className="mgr-transfer-section-title">New transfer request</h3>
          <div className="mgr-transfer-grid">
            <label className="mgr-transfer-field">
              <span>Receive at</span>
              <select
                className="mgr-transfer-input"
                value={recipientSiteId}
                onChange={(e) => {
                  trySelectSite(e.target.value, setRecipientSiteId, () => setInventoryItemId(''));
                }}
                required
              >
                <option value="">Select site…</option>
                {managerSites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.id}
                  </option>
                ))}
              </select>
            </label>
            <label className="mgr-transfer-field">
              <span>Send from</span>
              <select
                className="mgr-transfer-input"
                value={senderSiteId}
                onChange={(e) => trySelectSite(e.target.value, setSenderSiteId)}
                required
              >
                <option value="">Select site…</option>
                {senderOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.id}
                  </option>
                ))}
              </select>
            </label>
            <label className="mgr-transfer-field mgr-transfer-field-span2">
              <span>Product (recipient site brand inventory)</span>
              <select
                className="mgr-transfer-input"
                value={inventoryItemId}
                onChange={(e) => setInventoryItemId(e.target.value)}
                required
                disabled={!brandId}
              >
                <option value="">{brandId ? 'Select product…' : 'Choose a recipient site first'}</option>
                {productOptions.map((i) => (
                  <option key={i.id} value={i.id}>
                    {(i.name || i.itemName || '').trim() || i.id}
                    {i.pivotCategory ? ` — ${i.pivotCategory}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="mgr-transfer-field">
              <div className="mgr-transfer-qty-row">
                <label className="mgr-transfer-qty-label" htmlFor={qty1InputId}>
                  Quantity (unit 1)
                </label>
                {showUnit1Pill ? (
                  <span className="mgr-transfer-uom-pill" title="Unit of measure (catalog)">
                    {unit1Name}
                  </span>
                ) : null}
                <input
                  id={qty1InputId}
                  className="mgr-transfer-input mgr-transfer-qty-input"
                  type="text"
                  inputMode="decimal"
                  value={quantity1}
                  onChange={(e) => setQuantity1(e.target.value)}
                  placeholder="0"
                  required
                />
              </div>
            </div>
            <div className="mgr-transfer-field">
              <div className={'mgr-transfer-qty-row' + (!hasUnit2 ? ' mgr-transfer-qty-row--disabled' : '')}>
                <label className="mgr-transfer-qty-label" htmlFor={qty2InputId}>
                  Quantity (unit 2{hasUnit2 ? ', optional' : ''})
                </label>
                {hasUnit2 && unit2Name ? (
                  <span className="mgr-transfer-uom-pill" title="Unit of measure (catalog)">
                    {unit2Name}
                  </span>
                ) : null}
                <input
                  id={qty2InputId}
                  className="mgr-transfer-input mgr-transfer-qty-input"
                  type="text"
                  inputMode="decimal"
                  value={quantity2}
                  onChange={(e) => setQuantity2(e.target.value)}
                  placeholder=""
                  disabled={!hasUnit2}
                />
              </div>
            </div>
          </div>
          <div className="mgr-transfer-actions">
            <button type="submit" className="btn cr-btn-primary" disabled={submitting}>
              {submitting ? <Loader2 className="mgr-transfer-spin" size={16} /> : <Send size={16} />}
              {submitting ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </form>
      )}

      <div className="card mgr-transfer-table-card">
        <h3 className="mgr-transfer-section-title">Pending</h3>
        {pendingList.length === 0 ? (
          <p className="mgr-transfer-empty">No pending transfers.</p>
        ) : (
          <div className="mgr-table-wrap">
            <table className="mgr-transfer-table">
              <thead>
                <tr>
                  <th>From → To</th>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingList.map((t) => {
                  const senderSite = tenantSites.find((s) => s.id === t.senderSiteId);
                  const recipientSite = tenantSites.find((s) => s.id === t.recipientSiteId);
                  const senderMgr = senderSite?.managerId;
                  const recipientMgr = recipientSite?.managerId;
                  const senderBrandName =
                    senderSite && senderSite.brandId != null && senderSite.brandId !== ''
                      ? brandNameById[senderSite.brandId] || null
                      : null;
                  const recipientBrandName =
                    recipientSite && recipientSite.brandId != null && recipientSite.brandId !== ''
                      ? brandNameById[recipientSite.brandId] || null
                      : null;
                  const sameBrand =
                    senderBrandName && recipientBrandName && senderBrandName === recipientBrandName;
                  const iAmSenderMgr = senderMgr === uid;
                  const iAmRecipientInitiator = t.initiatedByUserId === uid && recipientMgr === uid;
                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="mgr-transfer-route">
                          <div className="mgr-transfer-route-line">
                            <div className="mgr-transfer-route-cell mgr-transfer-route-cell--from">
                              <span className="mgr-transfer-route-site">{t.senderSiteName}</span>
                            </div>
                            <div className="mgr-transfer-route-arrow-col" aria-hidden>
                              →
                            </div>
                            <div className="mgr-transfer-route-cell mgr-transfer-route-cell--to">
                              <span className="mgr-transfer-route-site">{t.recipientSiteName}</span>
                            </div>
                          </div>
                          {(senderBrandName || recipientBrandName) &&
                            (sameBrand ? (
                              <div className="mgr-transfer-brand-row-same">
                                <span className="mgr-transfer-brand-pill">{senderBrandName}</span>
                              </div>
                            ) : (
                              <div className="mgr-transfer-route-line mgr-transfer-route-line--brands">
                                <div className="mgr-transfer-route-cell mgr-transfer-route-cell--from">
                                  {senderBrandName ? (
                                    <span className="mgr-transfer-brand-pill">{senderBrandName}</span>
                                  ) : null}
                                </div>
                                <div className="mgr-transfer-route-arrow-col mgr-transfer-route-arrow-col--spacer" aria-hidden />
                                <div className="mgr-transfer-route-cell mgr-transfer-route-cell--to">
                                  {recipientBrandName ? (
                                    <span className="mgr-transfer-brand-pill">{recipientBrandName}</span>
                                  ) : null}
                                </div>
                              </div>
                            ))}
                        </div>
                      </td>
                      <td>{t.itemNameSnapshot || '—'}</td>
                      <td>
                        {t.quantity1}
                        {t.quantity2 != null && Number(t.quantity2) !== 0 ? ` / ${t.quantity2}` : ''}
                      </td>
                      <td>{t.status}</td>
                      <td className="mgr-transfer-actions-cell">
                        {iAmSenderMgr && (
                          <>
                            <button type="button" className="btn btn-sm cr-btn-primary" onClick={() => approve(t.id)}>
                              <CheckCircle size={14} /> Approve
                            </button>
                            <button type="button" className="btn btn-sm btn-secondary" onClick={() => setRejectId(t.id)}>
                              <XCircle size={14} /> Reject
                            </button>
                          </>
                        )}
                        {iAmRecipientInitiator && (
                          <button type="button" className="btn btn-sm btn-secondary" onClick={() => cancel(t.id)}>
                            Cancel
                          </button>
                        )}
                        {!iAmSenderMgr && !iAmRecipientInitiator && <span className="mgr-transfer-muted">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rejectId && (
        <div className="cr-modal-overlay">
          <div className="card cr-modal mgr-transfer-reject-modal">
            <h3 className="cr-modal-title">Reject transfer</h3>
            <textarea
              className="cr-review-textarea"
              placeholder="Reason (optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
            />
            <div className="cr-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setRejectId(null); setRejectReason(''); }}>
                Close
              </button>
              <button type="button" className="btn cr-btn-danger" onClick={reject}>
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {removeTransferId && (
        <div className="cr-modal-overlay">
          <div className="card cr-modal mgr-transfer-reject-modal">
            <h3 className="cr-modal-title">Remove approved transfer</h3>
            <p className="mgr-transfer-remove-copy">
              This permanently deletes this transfer for the current counting period. The recipient site manager is notified by chat.
            </p>
            <div className="cr-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={removing}
                onClick={() => setRemoveTransferId(null)}
              >
                Cancel
              </button>
              <button type="button" className="btn cr-btn-danger" disabled={removing} onClick={confirmRemoveTransfer}>
                {removing ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card mgr-transfer-table-card">
        <h3 className="mgr-transfer-section-title">History</h3>
        {historyList.length === 0 ? (
          <p className="mgr-transfer-empty">No completed or cancelled transfers yet.</p>
        ) : (
          <div className="mgr-table-wrap">
            <table className="mgr-transfer-table">
              <thead>
                <tr>
                  <th>From → To</th>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Value</th>
                  <th>Outcome</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {historyList.map((t) => (
                  <tr key={t.id}>
                    <td>
                      {t.senderSiteName} → {t.recipientSiteName}
                    </td>
                    <td>{t.itemNameSnapshot || '—'}</td>
                    <td>{t.pivotCategory || '—'}</td>
                    <td>{t.totalValue != null ? Number(t.totalValue).toFixed(2) : '—'}</td>
                    <td>{t.status}</td>
                    <td>{t.resolvedAt ? new Date(t.resolvedAt).toLocaleString() : '—'}</td>
                    <td>
                      {t.removable ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary mgr-transfer-remove-btn"
                          onClick={() => setRemoveTransferId(t.id)}
                          title="Remove this approved transfer for the current period"
                        >
                          <Trash2 size={14} aria-hidden /> Remove
                        </button>
                      ) : (
                        <span className="mgr-transfer-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerTransfers;
